// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import { EncryptionLevel, IPolicyControlCache, IPolicyFolders, ProtectionLevel } from 'src/types/d3ePolicy';
import {
  ProtectionLevelBottomSheetComponent
} from '../default-file-format/protection-level-bottom-sheet/protetction-level-bottom-sheet.component';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ImportExtsFoldersComponent } from 'src/app/dialogs/import-exts-folders/import-exts-folders.component';
import { MatDialog } from '@angular/material/dialog';
import authHelper from 'src/app/auth/authHelper';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  FILENAMES_REGEX,
  FOLDER_NAME_REGEXP,
  NO_EXE_OR_DLL_REGEXP,
  NO_SPACES_REGEXP,
  OTHER_VARS_REGEXP,
  ROOT_REGEX,
  UNC_PATH_REGEXP,
  VARIABLE_REGEX
} from '../../../../types/common';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-policy-folders',
    templateUrl: './policy-folders.component.html',
    styleUrls: ['./policy-folders.component.scss'],
    standalone: false
})
export class PolicyFoldersComponent implements OnChanges {
  @Input()show_environment_variables: boolean;
  @Input()policyFolders: IPolicyFolders = {
    disabledFolders: [],
    protectedFolders: [],
    lockedFolders: [],
    encryptedProtectedFolders: [],
    encryptedLockedFolders: []
  };
  @Input()enableSubscriptionKey: boolean = false;
  @Input()subKeyUUIDs: string[];
  @Output()policyFoldersChanged: EventEmitter<IPolicyFolders> = new EventEmitter<IPolicyFolders>();
  @Output()folderErrorStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  
  @ViewChildren('folderPathInputs', { read: ElementRef}) folderPathInputs: QueryList<ElementRef>;
  
  folderFormGroup: UntypedFormGroup;
  folders: UntypedFormArray;
  toggleGroupAppearance: 'legacy' | 'standard' = 'standard'; // make input?
  policyEnabled = true; // make input?
  selectedIndex: number | null = null;
  cachedControlValues: IPolicyControlCache[] = [];
  folderErrors: string[] = [];
  importErrors = 0;
  encryptionLevelButtons = false;
  fTopicDescription: string;
  fInvalid: string;
  fErrorDirConfigured: string;
  fInvalidSystemFile: string;

  constructor(
    private bottomSheet: MatBottomSheet,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService
  ) {
    this.translate.get(['TOPIC_DESCRIPTION_FOLDER', 'ERROR_DIRECTORY_CONFIGURED', 'ERROR_EMPTY_PATHS',
      'INVALID_SYSTEM_FILE']).subscribe(vals => {
      this.fTopicDescription = vals['TOPIC_DESCRIPTION_FOLDER'];
      this.fErrorDirConfigured = vals['ERROR_DIRECTORY_CONFIGURED'];
      this.fInvalidSystemFile = vals['INVALID_SYSTEM_FILE'];
    });
    this.show_environment_variables = false;
    this.encryptionLevelButtons  = authHelper.getFeatureFlagFromToken('elb') || false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.importErrors = 0;
    if (changes.subKeyUUIDs && changes.subKeyUUIDs.currentValue.previousValue !== changes.subKeyUUIDs.currentValue) {
      this.subKeyUUIDs = changes.subKeyUUIDs.currentValue;
    }
    if (changes.policyFolders && changes.policyFolders.currentValue) {

      this.cachedControlValues = [];

      if (this.folderFormGroup) {
        const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
        const length = folders.length;
        for (let i = 0; i < length; i++) {
          folders.removeAt(0);
        }
        this.folderErrors = [];
      }

      this.updatePolicyFolders(changes.policyFolders.currentValue);
    }

    if (changes.enableSubscriptionKey && changes.enableSubscriptionKey.currentValue) {
      this.enableSubscriptionKey = changes.enableSubscriptionKey.currentValue;
    }
  }

  onInputChange(event, control, index) {
    const folderPath = control.value.path;
    this.cachedControlValues[index].newValue = folderPath;
    this.validateDirectories(folderPath, 'directory', index);

    let protectionLevel: ProtectionLevel = control.value.setting.split('-')[0];
    const encryptionLevel: EncryptionLevel = control.value.encryptedSetting.split('-')[0];
    const protectionSetting = `${protectionLevel}-${index}`;
    const encryptionSetting = `${encryptionLevel}-${index}`;
    protectionLevel = encryptionLevel === 'encrypted' ? `${protectionLevel}-${encryptionLevel}` as ProtectionLevel : protectionLevel;

    // Remove previous value from the policyFolders
    this.removeDirectoryFromPolicy(this.cachedControlValues[index].setting, index);

    if (protectionLevel === 'medium') {
      // add new value
      this.policyFolders.protectedFolders.push(control.value.path);
      this.cachedControlValues[index].setting = 'medium';

    } else if (protectionLevel === 'high') {
      // add new value
      this.policyFolders.lockedFolders.push(control.value.path);
      this.cachedControlValues[index].setting = 'high';

    } else if (protectionLevel === 'medium-encrypted') {
      const encryptedProtectedDir = {'dir': control.value.path, 'subscription_keys': true, 'key_uuids': this.subKeyUUIDs};
      this.policyFolders.encryptedProtectedFolders.push(encryptedProtectedDir);
      this.cachedControlValues[index].setting = 'medium-encrypted';

    } else if (protectionLevel === 'high-encrypted') {
      // add new value
      const encryptedLockedFolders = {'dir': control.value.path, 'subscription_keys': true, 'key_uuids': this.subKeyUUIDs};
      this.policyFolders.encryptedLockedFolders.push(encryptedLockedFolders);
      this.cachedControlValues[index].setting = 'high-encrypted';

    } else {
      // add new value
      this.policyFolders.disabledFolders.push(control.value.path);
      this.cachedControlValues[index].setting = 'none';
    }

    this.cachedControlValues[index].originalValue = control.value.path;
    this.cachedControlValues[index].newValue = control.value.path;

    control.patchValue({
      path: control.value.path,
      setting: protectionSetting,
      encryptionSetting: encryptionSetting
    });

    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'directory', i);
      }
    });

    this.policyFoldersChanged.emit(this.policyFolders);

  }

  onFocusIn(event, index) {
    this.controls.forEach((c, i) => {
      if (index !== i) {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'directory', i);
      }
    });
  }

  onFocusOut(event, index) {
    this.validateDirectories(this.cachedControlValues[index].newValue, 'directory', index);
  }

  hasFolderError(i) {
    let hasError = false;
    if (this.folderErrors[i] !== null) {
      hasError = true;
    }
    return hasError;
  }

  updatePolicyFolders(policyFolders: IPolicyFolders): void {
    this.policyFolders = policyFolders;

    if (!this.policyFolders.disabledFolders) {
      this.policyFolders.disabledFolders = [];
    }
    // let insertedCount = 0;
    if (this.policyFolders.protectedFolders.length === 0 
      && this.policyFolders.lockedFolders.length === 0
      && this.policyFolders.disabledFolders.length === 0
      && this.policyFolders.encryptedProtectedFolders.length === 0
      && this.policyFolders.encryptedLockedFolders.length === 0) {
        this.folderFormGroup = this.formBuilder.group({
          folders: this.formBuilder.array([this.createFileFormatGroup('', 'medium-0', 'none-0', false)])
        });
        this.cachedControlValues = [];
        this.cachedControlValues.push({
          originalValue: '',
          newValue: '',
          editMode: false,
          setting: 'medium',
          encryptedSetting: 'none-encrypted'
        });
    } else {
      const allFolders: {'path': string, 'setting': ProtectionLevel}[] = [];

      this.policyFolders.disabledFolders.forEach(e => {
        allFolders.push({'path': e, 'setting': 'none'});
      });

      this.policyFolders.protectedFolders.forEach(e => {
        allFolders.push({'path': e, 'setting': 'medium'});
      });
      
      this.policyFolders.lockedFolders.forEach(e => {
        allFolders.push({'path': e, 'setting': 'high'});
      });

      this.policyFolders.encryptedProtectedFolders.forEach((e => {
        allFolders.push({'path': e.dir, 'setting': 'medium-encrypted'});
      }))

      this.policyFolders.encryptedLockedFolders.forEach((e => {
        allFolders.push({'path': e.dir, 'setting': 'high-encrypted'});
      }))

      allFolders.sort((a, b) => a.path.localeCompare(b.path));

      allFolders.forEach((value, index) => {
        const path = value.path;
        let setting = value.setting.split('-');
        let folderSetting = setting[0];
        const encryptedSetting = setting.length > 1 ? setting[1] as EncryptionLevel : 'none-encrypted';

        if (this.folderFormGroup) {
          this.addFolderFormGroup(path, `${folderSetting}-${index}`, `${encryptedSetting}-${index}`);
        } else {
          this.folderFormGroup = this.formBuilder.group({
            folders: this.formBuilder.array([this.createFileFormatGroup(path, `${folderSetting}-${index}`, `${encryptedSetting}-${index}`)])
          });
        }

        this.cachedControlValues.push({
          originalValue: path,
          newValue: path,
          editMode: false,
          setting: value.setting,
          encryptedSetting: encryptedSetting
        });

      });
    }
  }

  get controls()  {
    return (this.folderFormGroup.get('folders') as UntypedFormArray).controls;
  }

  onToggleChanged(event, control, index) {
    const folderSettings = event.value.split('-');
    const encryptedSetting = this.cachedControlValues[index].encryptedSetting;

    if (folderSettings.length === 2) {
      let protectionLevel = folderSettings[0] as ProtectionLevel;

      if (protectionLevel === 'none') {
        control.patchValue({
          encryptedSetting: `none-encrypted-${index}`
        });
      } else if (encryptedSetting === 'encrypted') {
        control.patchValue({
          setting: `${protectionLevel}-${index}`
        });
        protectionLevel = `${protectionLevel}-encrypted` as ProtectionLevel;
      } else {
        control.patchValue({
          setting: event.value
        });
      }

      this.updateFoldersValue(this.cachedControlValues[index].setting, protectionLevel, index);
      
      this.controls.forEach((c, i) => {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'directory', i);
      });

    }
  }

  onEncryptionToggleChanged(event, control, index) {
    const encryptionSettings = event.value.split('-');

    if (encryptionSettings.length >= 2) {
      const encryptionLevel: string = encryptionSettings[0];
      control.patchValue({
        encryptedSetting: event.value
      });
      let protectionLevel = this.cachedControlValues[index].setting.split('-')[0] as ProtectionLevel;
      if (encryptionLevel === 'encrypted') {
        protectionLevel = `${protectionLevel}-${encryptionLevel}` as ProtectionLevel;
      }

      this.updateFoldersValue(this.cachedControlValues[index].setting, protectionLevel, index);

      this.controls.forEach((c, i) => {
        const path = c.get('path').value || '';
        this.validateDirectories(path, 'directory', i);
      });
    }
  }

  onAddVariable(control, index: number, variable: string) {
    const path = control.value.path;
    const newPath = variable.concat(path);

    this.selectedIndex = index;
    this.cachedControlValues[index].newValue = newPath;

    this.validateDirectories(this.cachedControlValues[index].newValue, 'directory', index);

    if (this.folderErrors[index] === null) {
      // The following map() functions check to see if the variable being added is to an
      // existing path and if it is it updates the path with the new variable.
      this.policyFolders.disabledFolders = this.policyFolders.disabledFolders.map(
        p => p.toLowerCase().trim() === path.toLowerCase().trim() ? newPath : p);
  
      this.policyFolders.protectedFolders = this.policyFolders.protectedFolders.map(
        p => p.toLowerCase().trim() === path.toLowerCase().trim() ? newPath : p
      );
  
      this.policyFolders.lockedFolders = this.policyFolders.lockedFolders.map(
        p => p.toLowerCase().trim() === path.toLowerCase().trim() ? newPath : p
      );

      this.policyFolders.encryptedProtectedFolders = this.policyFolders.encryptedProtectedFolders.map(p => {
        if (p.dir.toLowerCase().trim() === path.toLowerCase().trim()) {
           return {dir: newPath, subscription_keys: true, key_uuids: p.key_uuids};
        } else {
          return {dir: p.dir, subscription_keys: true, key_uuids: p.key_uuids};
        }
      });

      this.policyFolders.encryptedLockedFolders = this.policyFolders.encryptedLockedFolders.map(p => {
        if (p.dir.toLowerCase().trim() === path.toLowerCase().trim()) {
           return {dir: newPath, subscription_keys: true, key_uuids: p.key_uuids};
        } else {
          return {dir: p.dir, subscription_keys: true, key_uuids: p.key_uuids};
        }
      });
    }

    control.patchValue({
        path: newPath
    });

    const protectionLevel = this.cachedControlValues[index].setting;

    this.updateFoldersValue(protectionLevel, protectionLevel, index);
    // need to set the original value here to this value or adding anything to the value
    // will not be detected in the onInputChange method and the variable will not get updated
    // correctly
    this.cachedControlValues[index].originalValue = this.cachedControlValues[index].newValue;

    this.policyFoldersChanged.emit(this.policyFolders);
  }

  updateFoldersValue(previousProtectionLevel: ProtectionLevel, newProtectionLevel: ProtectionLevel, index: number): void {

    if (index >= 0 && index < this.controls.length) {
      const path = this.controls[index].get('path').value.trim();
      const encryptedLevel: EncryptionLevel = newProtectionLevel.endsWith('encrypted') ? 'encrypted' : 'none-encrypted';
      if (path.length > 0) {
        this.removeDirectoryFromPolicy(previousProtectionLevel, index);

        switch (newProtectionLevel) {
          case 'medium':
            this.policyFolders.protectedFolders.push(path);
            break;
          case 'high':
            this.policyFolders.lockedFolders.push(path);
            break
          case 'medium-encrypted':
            const encryptedProtectedDir = {'dir': path, 'subscription_keys': true, 'key_uuids': this.subKeyUUIDs};
            this.policyFolders.encryptedProtectedFolders.push(encryptedProtectedDir);
            break;
          case 'high-encrypted':
            const encryptedLockedDir = {'dir': path, 'subscription_keys': true, 'key_uuids': this.subKeyUUIDs};
            this.policyFolders.encryptedLockedFolders.push(encryptedLockedDir);
            break;
          default:  // case 'none'
            this.policyFolders.disabledFolders.push(path);
            break;
        }
      }
      
      this.cachedControlValues[index].setting = newProtectionLevel;
      this.cachedControlValues[index].encryptedSetting = encryptedLevel;

      this.policyFoldersChanged.emit(this.policyFolders);
    }
  }

  removeDirectoryFromPolicy(protectionLevel: ProtectionLevel, index: number) {
    switch (protectionLevel) {
      case 'none':
        const disabledIndex = this.policyFolders.disabledFolders.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (disabledIndex >= 0) {
          this.policyFolders.disabledFolders.splice(disabledIndex, 1);
        }
        break;
      case 'medium':
        const protectedIndex = this.policyFolders.protectedFolders.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (protectedIndex >= 0) {
          this.policyFolders.protectedFolders.splice(protectedIndex, 1);
        }
        break;
      case 'high':
        const lockedIndex = this.policyFolders.lockedFolders.findIndex(p => 
          p.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (lockedIndex >= 0) {
          this.policyFolders.lockedFolders.splice(lockedIndex, 1);
        }
        break;
      case 'medium-encrypted':
        const mediumEncryptedIndex = this.policyFolders.encryptedProtectedFolders.findIndex(p =>
          p.dir.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (mediumEncryptedIndex >= 0) {
          this.policyFolders.encryptedProtectedFolders.splice(mediumEncryptedIndex, 1);
        }
        break;
      case 'high-encrypted':
        const highEncryptedIndex = this.policyFolders.encryptedLockedFolders.findIndex(p =>
          p.dir.toLocaleLowerCase() === this.cachedControlValues[index].originalValue.toLocaleLowerCase()
        );
        if (highEncryptedIndex >= 0) {
          this.policyFolders.encryptedLockedFolders.splice(highEncryptedIndex, 1);
        }
        break;
    }
  }

  public addFolderFormGroup(path: string = '', setting: string = '', encryptedSetting: string = '') {
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;

    let folderSetting = setting.trim();
    folderSetting = folderSetting !== '' ? setting : `medium-${folders.length}`;

    let encryptSetting = encryptedSetting.trim();
    encryptSetting = encryptSetting !== '' ? encryptSetting : `none-${folders.length}`;
    
    if (folders.value.some(v => v.path === '')) {
      return;
    }

    folders.push(this.createFileFormatGroup(path, folderSetting, encryptSetting));

  }

  public onAddFolderClick() {
    const path = '';
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    const protectionLevel = `medium`;
    const encryptionLevel = `none-encrypted`;
    const protectionSetting = `medium-${folders.length}`;
    const encryptionSetting = `none-encrypted-${folders.length}`;
    if (folders.value.some(v => v.path === '')) {
      // TODO: Warn user they have a new folder that needs a path before creating a new folder.
      return;
    }

    folders.push(this.createFileFormatGroup(path, protectionSetting, encryptionSetting, false));
    this.cachedControlValues.push({
      originalValue: path,
      newValue: path,
      editMode: true,
      setting: protectionLevel,
      encryptedSetting: encryptionLevel
    });
    // Make this dirty so we see enable the cancel/save buttons
    this.policyFoldersChanged.emit(this.policyFolders);
  }

  removePathRow(path, index: number) {
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    const previousProtectionLevel = this.cachedControlValues[index].setting;
    
    if (folders.length > 0 && index < folders.length) {
      folders.removeAt(index);

      this.removeDirectoryFromPolicy(previousProtectionLevel, index);
      this.cachedControlValues.splice(index, 1);

      if (folders.length === 0) {
        this.onAddFolderClick();
      }

      this.folderErrors.splice(index, 1);

      this.controls.forEach((c, i) => {
        const p = c.get('path').value || '';
        this.validateDirectories(p, 'directory', i);
      });

      this.policyFoldersChanged.emit(this.policyFolders);
    }
  }

  private createFileFormatGroup(path: string, setting: string, encryptedSetting: string, validate = true) {
    const length = this.folderErrors.push(null);
    const index = length - 1;
    if (validate) {
      this.validateDirectories(path, 'directory', index);
    }

    return new UntypedFormGroup({
      'path': new UntypedFormControl(path.trim(), Validators.nullValidator),
      'setting': new UntypedFormControl({value: setting, disabled: false}, Validators.nullValidator),
      'encryptedSetting': new UntypedFormControl({value: encryptedSetting, disabled: false}, Validators.nullValidator)
    });
  }

  validateDirectories(value: string, type: 'directory' | 'file', index: number): void {
    let error = null;
    const folderPath = value;
    let isUNCPath = false;

    if (!(this.cachedControlValues.length === 1 && folderPath === '')) {
      // Check if this is a UNC path
      isUNCPath = UNC_PATH_REGEXP.test(folderPath);

      // test the basic syntax of the line
      if (!isUNCPath && !FOLDER_NAME_REGEXP.test(folderPath)) {
        error = this.translate.instant('ERROR_INVALID_DIR_START', {
          type: type
        });
      }

      if (folderPath.trim().endsWith('\\') ) {
        error = this.translate.instant('ERROR_INVALID_ENDS_WITH', {
          type: type
        });
      }

      let driveAndPath;
      if (folderPath.includes(':')) {
        driveAndPath = folderPath.split(':');
      } else {
        // no : means it starts with a variable, fake out the drive letter here to allow for processing.
        // The drive letter is not looked at further down anyhow.
        driveAndPath = [ '', folderPath ];
      }

      if (this.cachedControlValues.length > index) {
        this.cachedControlValues.forEach((c, i) => {
          const newvalue = c.newValue.toLocaleLowerCase() + '||';
          const otherPath = folderPath.toLocaleLowerCase() + '||';
          if ((newvalue.startsWith(otherPath) || otherPath.startsWith(newvalue)) && i !== index) {
            error = this.fErrorDirConfigured;
          }
        });
      }

      if (driveAndPath.length > 1) {
        const path_without_drive = driveAndPath[1];
        let test_variables = path_without_drive;
        // make sure no special root files are specified
        if (ROOT_REGEX.test(path_without_drive.trim()) ) {
          error = `${this.fInvalidSystemFile}:  ${folderPath}`;
        } else if (VARIABLE_REGEX.test(test_variables)) {
            // allow only some variables, any other variables are NOT allowed.
            test_variables = test_variables.replace(VARIABLE_REGEX, 'replaced');
        }

        if (!isUNCPath && !OTHER_VARS_REGEXP.test(test_variables)) {
          error = this.translate.instant('ERROR_VARIABLE_PATH', {
            type: type
          });
        } else {
          const filenames = path_without_drive.split('\\');
          for (const filename of filenames) {
            if (FILENAMES_REGEX.test(filename.trim())) {
              error = this.translate.instant('ERROR_PATH_NO_DRIVE', {type, filename});
            }
            if (NO_EXE_OR_DLL_REGEXP.test(filename.trim())) {
              error = this.translate.instant('ERROR_PROHIBITED_EXTS', {
                type: type,
                filename: filename
              });
            }
            if (NO_SPACES_REGEXP.test(filename)) {
              error = this.translate.instant('ERROR_NO_SPACES', {
                type: type,
                filename: filename
              });
            }

          }
        }
      }

      if (folderPath.length < 1) {
        error = this.translate.instant('ERROR_EMPTY_PATHS');
        // error = this.fErrorEmptyPaths;
      }

    }

    this.folderErrors[index] = error;
    const hasError = !this.folderErrors.every(c => c === null);
    this.folderErrorStateChanged.emit(hasError);

  }
  
  onProtectionLevelClick() {
    this.bottomSheet.open(ProtectionLevelBottomSheetComponent, {
      data: {
        topicDescription: this.fTopicDescription
      },
      disableClose: false,
      closeOnNavigation: true,
      autoFocus: true,
      hasBackdrop: true,
    });
  }

  onEncryptionLevelClick() {
    this.bottomSheet.open(ProtectionLevelBottomSheetComponent, {
      data: {
        topicDescription: this.fTopicDescription,
        sheetType: 'encryption'
      },
      disableClose: false,
      closeOnNavigation: true,
      autoFocus: true,
      hasBackdrop: true,
    });
  }

  onImportClick() {
    const dialogRef = this.dialog.open(ImportExtsFoldersComponent, {
      width: '650px',
      height: '400px',
      data: {
        openedByExts: false
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        result[0].forEach(newFolder => {
          const isValidProtectionLevel = this.isValidProtectionLevel(newFolder.setting);
          const isValidEncryptionLevel = this.isValidEncryptionLevel(newFolder.encryptedSetting);
          if (isValidProtectionLevel && isValidEncryptionLevel) {
            this.onAddImportFolder(newFolder.value, newFolder.setting, newFolder.encryptedSetting);
          }
          else {
            this.importErrors++;
          }
        });
        this.importErrors = this.importErrors + result[1];
      }
    });
  }

  isValidProtectionLevel(setting: string) {
    if (setting === 'none') {
      return true;
    }
    if (setting === 'dynamic') {
      return true;
    }
    return setting === 'always on';
  }

  isValidEncryptionLevel(setting: string) {
    if (setting === 'none') {
      return true;
    }
    return setting === 'encrypted';
  }

  public getImportProtectionLevel(setting: string): ProtectionLevel {
    if (setting === 'none') {
      return `none`;
    }
    if (setting === 'dynamic') {
      return `medium`;
    }
    if (setting === 'always on') {
      return `high`;
    }
    return `medium`;
  }

  public getImportEncryptionLevel(setting: string): EncryptionLevel {
    if (setting === 'none') {
      return `none-encrypted`;
    }
    if (setting === 'encrypted') {
      return `encrypted`;
    }
    return `none-encrypted`;
  }

  getPolicyFolderGroup(setting: string, encryptionSetting: string): ProtectionLevel {
    setting = setting.trim();
    encryptionSetting = encryptionSetting.trim();

    if (setting === 'none') {
      return 'none';
    }
    if (setting === 'dynamic') {
      return encryptionSetting === 'encrypted' ? 'medium-encrypted' : 'medium';
    }
    if (setting === 'always on') {
      return encryptionSetting === 'encrypted' ? 'high-encrypted' : 'high';
    }
    return 'medium';
  }

  public onAddImportFolder(value: string, setting: string, encryptedSetting: string) {
    const path = value;
    const folders = this.folderFormGroup.get('folders') as UntypedFormArray;
    const newIndex = folders.length;
    const protectionLevel = this.getImportProtectionLevel(setting) as ProtectionLevel;
    const encryptionLevel = this.getImportEncryptionLevel(encryptedSetting) as EncryptionLevel;
    const protectionSetting = `${protectionLevel}-${folders.length}`;
    const encryptionSetting = `${encryptionLevel}-${folders.length}`;
    let found = false;

    const rawFolders = folders.getRawValue();
    const length = folders.length;
    for (let i = 0; i < length; i++) {
      const folderPath = rawFolders[i]['path'];
      if (folderPath === value) {
        found = true;
        this.controls[i].patchValue({
          setting: `${protectionLevel}-${i}`,
          encryptedSetting: `${encryptionLevel}-${i}`
        });
        this.updateFoldersValue(this.cachedControlValues[i].setting, this.getImportProtectionLevel(setting), i);
        this.validateDirectories(folderPath, 'directory', i);
      }
    }

    if (!found) {
      folders.push(this.createFileFormatGroup(path, protectionSetting, encryptionSetting, false));
      this.cachedControlValues.push({
        originalValue: path,
        newValue: path,
        editMode: true,
        setting: protectionSetting as ProtectionLevel,
        encryptedSetting: encryptionSetting as  EncryptionLevel
      });

      this.validateDirectories(path, 'directory', newIndex);
      switch (this.getPolicyFolderGroup(setting, encryptedSetting)) {
        case 'medium':
          this.policyFolders.protectedFolders.push(path);
          break;
        case 'high':
          this.policyFolders.lockedFolders.push(path);
          break;
        case 'medium-encrypted':
          const encryptedProtectedDir = {'dir': path, 'subscription_keys': true, 'key_uuids': []};
          this.policyFolders.encryptedProtectedFolders.push(encryptedProtectedDir);
          break;
        case 'high-encrypted':
          const encryptedLockedDir = {'dir': path, 'subscription_keys': true, 'key_uuids': []};
          this.policyFolders.encryptedLockedFolders.push(encryptedLockedDir);
          break;
        default:  // case 'none'
          this.policyFolders.disabledFolders.push(path);
          break;
      }

      this.policyFoldersChanged.emit(this.policyFolders);
    }
  }
}
